import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiKey, FiShield, FiArrowRight, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

interface LoginWithSeedProps {
    onClose: () => void;
}

const LoginWithSeed = ({ onClose }: LoginWithSeedProps) => {
    const { loginWithSeed, loginWithKey } = useAuth();
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const trimmedInput = inputValue.trim();
            const wordCount = trimmedInput.split(/\s+/).length;

            if (wordCount === 12) {
                loginWithSeed(trimmedInput);
            } else {
                loginWithKey(trimmedInput);
            }
            onClose();
        } catch (err: any) {
            setError(err.message || 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl relative"
            >
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                >
                    <FiX className="text-xl" />
                </button>

                <div className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-mavi-500/10 dark:bg-mavi-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <FiKey className="text-3xl text-mavi-500" />
                        </div>
                        <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mb-2">Giriş Yap</h2>
                        <p className="text-gray-500 dark:text-surface-400 font-merriweather text-sm">
                            12 kelimenizi veya özel anahtarınızı (nsec) buraya yapıştırın.
                        </p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-poppins font-black text-gray-400 dark:text-surface-500 uppercase tracking-widest px-1">
                                Kelimeler veya Anahtar
                            </label>
                            <textarea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                rows={3}
                                className="w-full bg-surface-50 dark:bg-surface-950 border border-surface-200 dark:border-surface-800 rounded-2xl p-4 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-surface-700 focus:outline-none focus:border-mavi-500 transition-all font-mono text-sm resize-none shadow-inner"
                                placeholder="Örn: kelime1 kelime2..."
                                required
                            />
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-kirmizi-50 dark:bg-kirmizi-900/10 border border-kirmizi-200 dark:border-kirmizi-800/20 p-4 rounded-xl text-kirmizi-600 dark:text-kirmizi-400 text-sm font-medium"
                            >
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !inputValue.trim()}
                            className="w-full bg-mavi-500 hover:bg-mavi-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-poppins font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 group shadow-lg shadow-mavi-500/20"
                        >
                            {isLoading ? 'Giriş Yapılıyor...' : 'Devam Et'}
                            {!isLoading && <FiArrowRight className="group-hover:translate-x-1 transition-transform" />}
                        </button>
                    </form>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-surface-50 dark:bg-surface-800/50 rounded-2xl border border-surface-100 dark:border-surface-800">
                            <FiShield className="text-xl text-green-500 shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-500 dark:text-surface-400 font-merriweather leading-relaxed">
                                <span className="font-poppins font-bold text-gray-900 dark:text-white block mb-1 uppercase tracking-tighter">Güvenli Giriş</span>
                                Girdiğiniz bilgiler asla sunucularımıza gönderilmez. Anahtar türetme işlemi tamamen tarayıcınızda yapılır.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-surface-50 dark:bg-surface-950 border-t border-surface-100 dark:border-surface-800 text-center">
                    <button
                        onClick={onClose}
                        className="text-xs font-poppins font-bold text-gray-400 hover:text-gray-600 dark:hover:text-surface-300 transition-colors uppercase tracking-widest"
                    >
                        Vazgeç ve Kapat
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default LoginWithSeed;
